/**
 * POS Cart & Receipt Component
 */

import React from 'react'
import {
	Paper,
	Box,
	Typography,
	Divider,
	Button,
	alpha,
	TextField,
	Avatar,
	ButtonGroup,
	Switch,
	Tooltip,
} from '@mui/material'
/** TYPES */
import { PosPayment, PosTotals, type CartItem } from '@/types/pos'
/** ICONS */
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import DiscountIcon from '@mui/icons-material/LocalOfferOutlined'
import AddDiscountIcon from '@mui/icons-material/PercentOutlined'
import DeleteXIcon from '@mui/icons-material/CancelOutlined'
import PrintIcon from '@mui/icons-material/PrintOutlined'
import DeleteIcon from '@mui/icons-material/DeleteForeverOutlined'
import ItemIcon from '@mui/icons-material/Inventory'
import CashIcon from '@mui/icons-material/AttachMoney'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import MobilePaymentIcon from '@mui/icons-material/PhoneIphone'
import QRIcon from '@mui/icons-material/QrCode'
import OtherPayment from '@mui/icons-material/MoreHoriz'
import { NumericFormat } from 'react-number-format'
import QuantityPicker from '../Button/QuantityPicker'
import ModernActionGroup from '../Button/ActionButton'
import AddDiscountDialog from './add.discount.dialog'
import { CheckoutButton } from '../Button/CheckoutButton'

interface POSCartProps {
	cartItems: CartItem[]
	totalAmount: number
	totals: PosTotals
	onCheckout: () => void
	onUpdateCart?: (items: CartItem[]) => void
	payments?: PosPayment[]
	onPaymentsChange?: (payments: PosPayment[]) => void
	remainingAmount?: number
	onComplete?: () => void
	disabled?: boolean
	onDelete?: (itemId: string | number) => void
}

const defaultPaymentMethods = [
	{
		method: 'cash',
		label: 'Бэлэн',
		icon: <CashIcon color="inherit" sx={{ verticalAlign: 'middle' }} fontSize="small" />,
	},
	{
		method: 'card',
		label: 'Карт',
		icon: <CreditCardIcon color="inherit" sx={{ verticalAlign: 'middle' }} fontSize="small" />,
	},
	{
		method: 'qpay',
		label: 'QPay төлбөр',
		icon: <QRIcon color="inherit" sx={{ verticalAlign: 'middle' }} fontSize="small" />,
	},
	{
		method: 'mobile',
		label: 'Мобайл төлбөр',
		icon: <MobilePaymentIcon color="inherit" sx={{ verticalAlign: 'middle' }} fontSize="small" />,
	},
	{
		method: 'other',
		label: 'Бусад',
		icon: <OtherPayment color="inherit" sx={{ verticalAlign: 'middle' }} fontSize="small" />,
	},
]

const POSCart = (props: POSCartProps) => {
	const [paymentMethods, setPaymentMethods] = React.useState(defaultPaymentMethods)
	const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState<string | null>(null)

	const [addingDiscountToItemId, setAddingDiscountToItemId] = React.useState<string | null>(null)
	const [addingDiscountToCart, setAddingDiscountToCart] = React.useState<boolean>(false)

	const [printReceipt, setPrintReceipt] = React.useState<boolean>(true)
	const [vatType, setVatType] = React.useState<'individual' | 'company'>('individual')
	const [vatRegNumber, setVatRegNumber] = React.useState<string>('')

	//const [payments, setPayments] = React.useState<PosPayment[]>([])
	const [payingAmount, setPayingAmount] = React.useState<number>(0)
	const [transactionsAmount, setTransactionsAmount] = React.useState<number>(0)
	// const [totals, setTotals] = React.useState<PosTotals>({
	// 	subtotal: 0,
	// 	discountTotal: 0,
	// 	vatAmount: 0,
	// 	totalPayable: 0,
	// })

	const totals = props.totals
	const payments = React.useMemo(() => props.payments || [], [props.payments])

	React.useEffect(() => {
		console.log(`mounted`)
	}, [])

	React.useEffect(() => {
		console.log(`cart items changed`)
	}, [props.cartItems])

	React.useEffect(() => {
		console.log(props.totals)
	}, [props.totals])

	React.useEffect(() => {
		setPayingAmount(props.remainingAmount || 0)
	}, [props.remainingAmount])

	React.useEffect(() => {
		const total = payments.reduce((sum, payment) => sum + payment.amount, 0)
		setTransactionsAmount(total)
	}, [payments])

	const handlePaymentChange = (method: string, amount: number) => {
		let updatedPayments = payments.filter((p) => p.method !== method)
		if (amount > 0) {
			updatedPayments.push({ method: method as PosPayment['method'], amount })
		}
		//setPayments(updatedPayments)
		if (props.onPaymentsChange) {
			props.onPaymentsChange(updatedPayments)
		}
	}

	const handleRemoveItem = (itemId: string | number) => {
		if (props.onDelete) {
			props.onDelete(itemId)
		}
	}

	const transactionId = React.useMemo(() => {
		return `#${Math.floor(Math.random() * 1000000)}`
	}, [])

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
			}}
		>
			<Box id="sidebar-cart-header" padding={1}>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
					}}
				>
					<ShoppingCartIcon sx={{ mr: 1, verticalAlign: 'middle' }} color="secondary" fontSize="small" />
					<Typography variant="h6" component="span">
						Одоогийн захиалга
					</Typography>
				</Box>
				<Box>
					<Typography variant="body2" color="textSecondary">
						гүйлгээний дугаар: {transactionId}
					</Typography>
				</Box>
			</Box>
			<Divider sx={{ my: 1 }} />
			{(addingDiscountToItemId || addingDiscountToCart) && (
				<AddDiscountDialog
					onApplyDiscount={(discount) => {
						// Apply discount to item or cart
					}}
					currentDiscountId={
						props.cartItems.find((item) => item.id === addingDiscountToItemId)?.discounts?.[0]?.id || null
					}
					onClose={() => {
						setAddingDiscountToItemId(null)
						setAddingDiscountToCart(false)
					}}
					open
					cartItem={props.cartItems.find((item) => item.id === addingDiscountToItemId)}
				/>
			)}
			<Box
				id="sidebar-cart-items"
				sx={{
					flexGrow: 1,
					overflowY: 'auto',
					maxHeight: '50vh',
					p: 1,
					// align center
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
				}}
			>
				{props.cartItems.length === 0 ? (
					<Typography variant="body2" color="textSecondary">
						Сагс хоосон байна.
					</Typography>
				) : (
					props.cartItems.map((item) => (
						<Box key={item.id} sx={{ width: '100%', mb: 1 }}>
							<CartItemRow
								item={item}
								onUpdate={(updatedItem) => {
									// Update item in cart
									const updatedItems = props.cartItems.map((ci) =>
										ci.id === updatedItem.id ? updatedItem : ci,
									)
									// Call onUpdateCart with updated items
									if (props.onUpdateCart) {
										props.onUpdateCart(updatedItems)
									}
								}}
								onRemove={(itemId) => {
									handleRemoveItem(itemId)
								}}
								onAddDiscount={(itemId) => setAddingDiscountToItemId(itemId as string)}
							/>
						</Box>
					))
				)}
			</Box>
			{payments.length > 0 && (
				<Box
					id="sidebar-cart-payments"
					sx={{
						p: 1,
						borderTop: 1,
						borderColor: 'divider',
						backgroundColor: (theme) => (theme.palette.mode === 'light' ? 'grey.100' : 'grey.900'),
					}}
				>
					<Box>
						<Typography variant="subtitle1" sx={{ mb: 1 }} fontSize={14}>
							Төлбөрийн гүйлгээнүүд ({payments.length})
						</Typography>
					</Box>
					{payments.map((payment, index) => (
						<Paper variant="outlined" key={index} sx={{ p: 1, mb: 1 }}>
							<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
								<Typography variant="body2">
									{payment.method.charAt(0).toUpperCase() + payment.method.slice(1)}
								</Typography>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										gap: 1,
									}}
								>
									<Typography variant="body2">
										<NumericFormat
											value={payment.amount}
											displayType="text"
											thousandSeparator
											decimalScale={2}
										/>
										₮
									</Typography>
									<Box
										sx={{
											mt: 0.5,
											cursor: 'pointer',
											transition: 'color 0.2s ease-in-out',
											'&:hover': { color: 'error.main' },
										}}
										onClick={() => {
											// Remove this payment
											const updatedPayments = payments.filter((p, i) => i !== index)
											if (props.onPaymentsChange) {
												props.onPaymentsChange(updatedPayments)
											}
										}}
									>
										<DeleteXIcon fontSize="small" />
									</Box>
								</Box>
							</Box>
						</Paper>
					))}
				</Box>
			)}
			<Box
				id="sidebar-cart-footer"
				sx={{
					// Stick to bottom
					mt: 'auto',
					p: 2,
					backgroundColor: (theme) => (theme.palette.mode === 'light' ? 'grey.100' : 'grey.900'),
					borderTop: 1,
					borderColor: 'divider',
					// shadow to top
					boxShadow: `0 -2px 8px ${alpha('#000', 0.1)}`,
					position: 'relative',
					bottom: 0,
				}}
			>
				<Box>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
						<Typography variant="body2">Нийт</Typography>
						<Typography variant="body1">
							<NumericFormat
								value={props.totals.subtotal}
								displayType="text"
								thousandSeparator
								decimalScale={2}
							/>{' '}
							₮
						</Typography>
					</Box>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								'&:hover': {
									cursor: 'pointer',
									textDecoration: 'underline',
								},
							}}
						>
							<DiscountIcon sx={{ verticalAlign: 'middle', mr: 0.5, color: 'green' }} fontSize="small" />
							<Typography variant="body2" component="span" color={'green'}>
								Хямдрал
							</Typography>
						</Box>
						<Typography variant="body1" color="error">
							<NumericFormat
								value={props.totals.discountTotal}
								displayType="text"
								decimalScale={2}
								thousandSeparator
							/>{' '}
							₮
						</Typography>
					</Box>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
						<Typography variant="body2">Төлөх дүн</Typography>
						<Typography variant="body1" color="primary">
							<NumericFormat
								value={props.totals.totalPayable}
								displayType="text"
								decimalScale={2}
								thousandSeparator
							/>{' '}
							₮
						</Typography>
					</Box>
				</Box>
				<Box id="sidebar-cart-payment-methods">
					<Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
						Төлбөрийн арга сонгох:
					</Typography>
					<Box
						sx={{
							display: 'grid',
							// 3 columns
							gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
							gap: 0.5,
						}}
					>
						{paymentMethods.map((method) => (
							<PaymentMethod
								key={method.method}
								method={method.label}
								icon={method.icon}
								selected={selectedPaymentMethod === method.method}
								onSelect={() => setSelectedPaymentMethod(method.method)}
								disabled={
									payments.some((p) => p.method === method.method) ||
									transactionsAmount >= totals.totalPayable
								}
							/>
						))}
					</Box>
					<Box>
						<NumericFormat
							customInput={TextField}
							label="Төлсөн дүн"
							variant="outlined"
							value={payingAmount}
							onValueChange={(values) => {
								setPayingAmount(values.floatValue ?? 0)
							}}
							fullWidth
							suffix="₮"
							thousandSeparator
							sx={{ mt: 2, backgroundColor: 'background.paper' }}
						/>
					</Box>
				</Box>
				<Box id="cart-ebarimt-vat">
					<Paper variant="outlined" sx={{ mt: 2 }}>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								borderBottom: 1,
								borderColor: 'divider',
								padding: 1,
							}}
						>
							<ButtonGroup variant="text" fullWidth disableElevation size="small">
								<Button
									onClick={() => setVatType('individual')}
									variant={vatType === 'individual' ? 'contained' : 'text'}
									size="small"
								>
									Хувь хүн
								</Button>
								<Button
									onClick={() => setVatType('company')}
									variant={vatType === 'company' ? 'contained' : 'text'}
									size="small"
								>
									Байгууллага
								</Button>
							</ButtonGroup>
						</Box>
						<Box padding={1}>
							<TextField
								label={vatType === 'company' ? 'Регистрийн дугаар' : 'Түргэн дугаар'}
								variant="outlined"
								fullWidth
								sx={{ mt: 1, mb: 1 }}
								size="small"
							/>
						</Box>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								padding: 1,
								pb: 0,
								pt: 0,
								borderTop: 1,
								borderColor: 'divider',
							}}
						>
							<Box>
								<PrintIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} fontSize="small" />
								<Typography variant="body2" component="span">
									eБаримт хэвлэх
								</Typography>
							</Box>
							<Box>
								<Switch
									checked={printReceipt}
									onChange={(e) => setPrintReceipt(e.target.checked)}
									color="primary"
								/>
							</Box>
						</Box>
					</Paper>
				</Box>
				{transactionsAmount === totals.totalPayable ? (
					/* Checkout Button */
					<CheckoutButton
						onCheckout={() => {}}
						color="success"
						size="small"
						label="Төлбөр төлөх"
						sx={{ mt: 2 }}
					/>
				) : (
					<Button
						variant="contained"
						color={
							payingAmount === totals.totalPayable || props.cartItems.length === 0 ? 'primary' : 'success'
						}
						disableElevation
						fullWidth
						sx={{ mt: 2 }}
						onClick={() => {
							handlePaymentChange(selectedPaymentMethod || 'cash', payingAmount)
						}}
						disabled={props.cartItems.length === 0}
					>
						{payingAmount === totals.totalPayable ? 'Төлөх' : 'Төлбөрийг баталгаажуулах'} (
						{<NumericFormat value={payingAmount} displayType="text" thousandSeparator decimalScale={2} />})
					</Button>
				)}
			</Box>
		</Box>
	)
}

const CartItemRow = React.memo(
	(props: {
		item: CartItem
		onUpdate: (item: CartItem) => void
		onRemove: (itemId: string | number) => void
		onAddDiscount?: (itemId: string | undefined) => void
	}) => {
		return (
			<Paper
				variant="outlined"
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					p: 1,
					gap: 2,
				}}
			>
				<Box>
					<Box>
						<Avatar
							sx={{
								// square
								borderRadius: 1,
								backgroundColor: alpha('#86198f', 0.1),
								color: 'primary.main',
								width: 30,
								height: 30,
							}}
						>
							<ItemIcon />
						</Avatar>
					</Box>
				</Box>
				<Box sx={{ flexGrow: 1 }}>
					<Typography variant="body1" fontSize={14}>
						{props.item.name ?? props.item.label ?? props.item.description}
					</Typography>
					<Typography variant="body2" color="textSecondary" fontSize={12}>
						<NumericFormat value={props.item.price} displayType="text" thousandSeparator decimalScale={2} />{' '}
						₮ x {props.item.quantity} ={' '}
						{props.item.discountedAmount && props.item.discountedAmount > 0 ? (
							<>
								<Typography
									variant="body2"
									component="span"
									sx={{ textDecoration: 'line-through', color: 'error.main', mr: 0.5 }}
									fontSize={12}
								>
									<NumericFormat
										value={props.item.price * props.item.quantity}
										displayType="text"
										thousandSeparator
										decimalScale={2}
									/>{' '}
									₮
								</Typography>
								<Typography variant="body2" component="span" color="primary.main" fontSize={12}>
									<NumericFormat
										value={props.item.price * props.item.quantity - props.item.discountedAmount}
										displayType="text"
										thousandSeparator
										decimalScale={2}
									/>{' '}
									₮
								</Typography>
							</>
						) : (
							<>
								<NumericFormat
									value={props.item.price * props.item.quantity}
									displayType="text"
									thousandSeparator
									decimalScale={2}
								/>{' '}
								₮
							</>
						)}
					</Typography>
				</Box>
				<Box
					sx={{
						width: '100px',
						display: 'flex',
						justifyContent: 'flex-end',
						gap: 1,
					}}
				>
					<QuantityPicker
						value={props.item.quantity ?? 1}
						size="small"
						min={1}
						disabled={props.item.price === 0}
						onChange={(quantity) => {
							// update item quantity
							const updatedItem = {
								...props.item,
								quantity,
								total: quantity * (props.item.unitPrice ?? props.item.price),
							}
							props.onUpdate(updatedItem)
						}}
					/>
					<ModernActionGroup
						size="small"
						actions={[
							{
								icon: <AddDiscountIcon />,
								label: 'Хөнгөлөлт нэмэх',
								key: 'add-discount',
								color:
									props.item?.discountedAmount && props.item.discountedAmount > 0
										? 'success'
										: 'inherit',
								onClick: () => {
									props.onAddDiscount && props.onAddDiscount(props.item.id ?? '')
								},
							},
							{
								icon: <DeleteIcon />,
								label: 'Сагснаас хасах',
								key: 'remove-item',
								color: 'error',
								onClick: () => {
									props.onRemove(props.item.id ?? '')
								},
							},
						]}
					/>
				</Box>
			</Paper>
		)
	},
)

const PaymentMethod = (props: { method: string; icon?: React.ReactNode; selected: boolean; onSelect: () => void; disabled?: boolean }) => {
	return (
		<Tooltip title={props.method} arrow>
			<Box
				sx={{
					border: 1,
					borderColor: props.selected ? 'primary.main' : 'divider',
					borderRadius: 2,
					p: 1,
					mb: 1,
					cursor: props.disabled ? 'not-allowed' : 'pointer',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					backgroundColor: props.selected
						? alpha('#86198f', 0.1)
						: props.disabled
						? 'grey.200'
						: 'background.paper',
					flexDirection: 'column',
					textAlign: 'center',
					'&:hover': {
						backgroundColor: props.disabled
							? undefined
							: alpha('#86198f', 0.05),
					},
					userSelect: 'none',
					opacity: props.disabled ? 0.5 : 1,
				}}
				onClick={!props.disabled && props.onSelect ? props.onSelect : undefined}
			>
				<Box
					sx={{
						color: props.selected ? 'primary.main' : 'textPrimary',
					}}
				>
					{props.icon}
				</Box>
				{/* <Typography variant="body1" color={props.selected ? 'primary' : 'textPrimary'} fontSize={12}>
				{props.method}
			</Typography> */}
			</Box>
		</Tooltip>
	)
}

export default POSCart
